import { Card } from 'antd';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import {
  UserOutlined,
  HeartOutlined,
  MedicineBoxOutlined,
  ThunderboltOutlined,
  LockOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useGetUserAddOnsQuery } from '../../../services/membership';
import './styles.scss';

interface UserOutletContext {
  userData?: {
    hasMembership?: boolean;
    currentMembership?: {
      status?: string;
    };
    memberships?: { status?: string }[];
  };
}

interface Service {
  id: number;
  name: string;
  value: string;
  icon: React.ReactNode;
  requiresMembership: boolean;
}

const services: Service[] = [
  { id: 1, name: 'Personal Training', value: 'personal-training', icon: <UserOutlined />,         requiresMembership: true  },
  { id: 2, name: 'Pilates',           value: 'pilates',           icon: <HeartOutlined />,         requiresMembership: false },
  { id: 3, name: 'Therapy',           value: 'therapy',           icon: <MedicineBoxOutlined />,   requiresMembership: false },
  { id: 4, name: 'EMS',               value: 'ems',               icon: <ThunderboltOutlined />,   requiresMembership: false },
  { id: 5, name: 'Paid Locker',       value: 'paid-locker',       icon: <LockOutlined />,          requiresMembership: true  },
  { id: 6, name: 'MMA',               value: 'mma',               icon: <TrophyOutlined />,        requiresMembership: false },
];

const SelectAddOnService = () => {
  const navigate          = useNavigate();
  const { id }            = useParams<{ id: string }>();
  const { userData }      = useOutletContext<UserOutletContext>();

  const { data: addOnsData } = useGetUserAddOnsQuery(id, { skip: !id });

  // A membership-requiring service should only show when the member has an
  // ACTIVE membership — hasMembership stays true even when the current
  // membership is inactive/expired, so check the actual status. `currentMembership`
  // isn't reliable for this: it can point at the most-recently-created membership
  // (e.g. a trial bought after the real membership), which may be inactive while
  // an earlier membership in `memberships` is still active. So check the whole list.
  const memberships = userData?.memberships ?? [];
  const hasStatus = (statuses: string[]) =>
    memberships.some(m => statuses.includes(m.status as string)) ||
    statuses.includes(userData?.currentMembership?.status as string);
  const hasActiveMembership = !!userData?.hasMembership && hasStatus(['active']);
  // Personal Training is also allowed for an "advance" (booked-ahead) membership.
  const hasActiveOrAdvanceMembership =
    !!userData?.hasMembership && hasStatus(['active', 'advance']);

  // Service types the user already has an ACTIVE add-on for. A service card is
  // hidden if the member already owns that add-on. Inactive/expired add-ons
  // don't count, so the member can re-purchase them.
  const ownedServiceTypes = new Set<string>();
  ((addOnsData as any)?.data ?? []).forEach((a: any) => {
    const status = String(a?.status ?? '').toLowerCase();
    if (status !== 'active' && status !== 'frozen' && status !== 'freezed') return;
    // normalise hyphens/underscores/spaces so "paid-locker" ↔ "paid_locker" match
    const norm = (v: any) => String(v ?? '').toLowerCase().replace(/[-_\s]/g, '');
    ownedServiceTypes.add(norm(a?.addonType));
    // lockers may carry type/addonType of "locker" — map them to the paid-locker card
    if (norm(a?.type) === 'locker' || norm(a?.addonType).includes('locker')) {
      ownedServiceTypes.add('paidlocker');
    }
  });

  const filteredServices = services.filter(s => {
    if (s.requiresMembership) {
      const allowed = s.value === 'personal-training' ? hasActiveOrAdvanceMembership : hasActiveMembership;
      if (!allowed) return false;
    }
    return !ownedServiceTypes.has(s.value.replace(/[-_\s]/g, ''));
  });

  return (
    <div className="select-addon-service-container">
      <h3 className="section-title">Select Add-On Service</h3>
      <div className="service-grid">
        {filteredServices.map(service => (
          <Card
            key={service.id}
            className="service-card"
            hoverable
            onClick={() => navigate(`/user-detail/${id}/buy-addon-service?type=${service.value}`)}
          >
            <div className="service-icon">{service.icon}</div>
            <h3 className="service-name">{service.name}</h3>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SelectAddOnService;
